import { Box } from '@mui/material';
import { useGame } from 'contexts/Game/Game';
import { useSettings } from 'contexts/Settings/Settings';
import _ from 'lodash';
import { useMemo } from 'react';
import { useWindowSize } from 'usehooks-ts';
import FunnelConversion from '../FunnelConversion/FunnelConversion';
import FunnelRetention from '../FunnelRetention/FunnelRetention';
import FunnelStep from '../FunnelStep/FunnelStep';
import { FunnelAddStepButton, FunnelGroup } from './Funnel.styles';

export default function Funnel({
    collapse,
    disableBounds,
    disableInputs,
    disableZooms,
    hideConversions,
    hideStepBody,
    hideSteps,
    highlightStep,
    rangeMode,
    retentionableStep,
    retentionedStep,
    retentioningStep,
    showableStep,
    updateInitials,
    zoomableConversion,
    zoomableRetention,
    zoomableStep,
    zoomedConversion,
    zoomedRetention,
    zoomedStep,
    beforeStepUpdate,
    onStepShow,
    onRetentionAdd,
    onRetentionConnect,
    onStepUpdate,
    onZoom,
}: FunnelProps) {

    const game = useGame();
    const settings = useSettings();
    const windowSize = useWindowSize();

    const highlightedStep = useMemo(() => {
        return highlightStep !== undefined ? game.steps[highlightStep] : null;
    }, [
        highlightStep,
        game.steps,
    ]);

    const zoomedInStep = useMemo(() => {
        return zoomedStep !== undefined ? game.steps[zoomedStep] : null;
    }, [
        game.steps,
        zoomedStep,
    ]);

    const zoomedInConversionStep = useMemo(() => {
        return zoomedConversion !== undefined ? game.steps[zoomedConversion] : null;
    }, [
        game.steps,
        zoomedConversion,
    ]);

    const zoomedInRetentionStep = useMemo(() => {
        return zoomedRetention !== undefined ? game.steps[zoomedRetention] : null;
    }, [
        game.steps,
        zoomedRetention,
    ]);

    async function updateStep(index: number, data: GameStepData) {
        const trigger = highlightedStep?.index === index || retentionedStep === index || zoomedInStep?.index === index || zoomedInConversionStep?.index === index || zoomedInRetentionStep?.index === index;

        // Prepare data
        const stepData = (trigger && beforeStepUpdate?.(data)) || data;
        if (updateInitials) {
            stepData.initial = stepData.max;
        }

        // Update step
        const step = await game.updateStep(index, stepData, updateInitials);

        // Trigger listener
        if (trigger) {
            onStepUpdate?.(step);
        }
    }

    const offset = useMemo(() => {
        const value = 1 - ((windowSize.width - 1024) / 500);
        return _.clamp(value, 0, 1) * -60;
    }, [
        windowSize.width,
    ]);

    return (
        <Box
            sx={{
                height: '100%',
                position: 'relative',
                left: offset
            }}>
            <Box
                sx={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: (hideConversions && !zoomedInConversionStep && !zoomedInRetentionStep && !zoomedInStep && !settings.debugFunnel) ? 2 : 0,
                    justifyContent: 'center',
                    position: 'relative',
                    zIndex: 1
                }}>
                {game.steps.map((step, index) => (
                    <FunnelGroup
                        $collapse={((collapse && index !== 0) || (!!zoomedInStep && zoomedInStep.index !== index) || (!!zoomedInConversionStep && zoomedInConversionStep.index !== index && zoomedInConversionStep.index !== index - 1) || (!!zoomedInRetentionStep && zoomedInRetentionStep.index !== index && zoomedInRetentionStep.index !== index + 1)) && !settings.debugFunnel}
                        $enabled={!highlightedStep || highlightedStep.index === index || settings.debugFunnel}
                        $index={index}
                        $inverse={(!!zoomedInConversionStep && zoomedInConversionStep.index > index) || (!!zoomedInRetentionStep && zoomedInRetentionStep.index > index)}
                        id={`funnel-group-${index}`}
                        key={index}
                    >
                        <FunnelStep
                            disableBounds={disableBounds}
                            disableInputs={(disableInputs || zoomedConversion === index || zoomedRetention === index + 1) && !settings.debugFunnel}
                            disableZooms={disableZooms}
                            hide={hideSteps?.includes(index)}
                            hideBody={hideStepBody || (highlightedStep && highlightedStep.index < index) || (!!zoomedInStep && zoomedInStep.index !== index)}
                            highlight={highlightedStep?.index === index}
                            rangeMode={rangeMode}
                            step={step}
                            zoomable={zoomableStep === index}
                            zoomed={zoomedStep === index}
                            onChange={data => updateStep(index, data)}
                            onZoom={onZoom}
                        />
                        {(index < game.steps.length - 1) && ((!collapse && !hideConversions && (!zoomedInConversionStep || zoomedConversion === index) && (!zoomedInRetentionStep || zoomedRetention === index + 1)) || settings.debugFunnel) && (
                            <FunnelConversion
                                disableInputs={disableInputs && !settings.debugFunnel}
                                disableZooms={disableZooms}
                                hideBody={zoomedRetention !== undefined && zoomedRetention === index - 1}
                                rangeMode={rangeMode}
                                step={step}
                                zoomable={zoomableConversion === index}
                                zoomed={zoomedConversion === index}
                                onChange={conversion => updateStep(index, { conversion })}
                                onZoom={onZoom}
                            />
                        )}
                        {(retentionableStep === index || retentioningStep === index || retentionedStep === index || retentionedStep === index + 1) && (
                            <FunnelRetention
                                adding={retentionableStep === index}
                                connected={retentionedStep === index}
                                connecting={retentioningStep === index}
                                disableInputs={disableInputs}
                                disableZooms={disableZooms}
                                step={step}
                                zoomable={zoomableRetention === index}
                                zoomed={zoomedRetention === index}
                                onAdd={() => retentionableStep === index && onRetentionAdd?.(step)}
                                onConnect={() => retentioningStep === index && onRetentionConnect?.(step)}
                                onChange={retention => updateStep(index, { retention })}
                                onZoom={onZoom}
                            />
                        )}
                        {showableStep === index && (
                            <FunnelAddStepButton onClick={() => onStepShow?.(step)} />
                        )}
                    </FunnelGroup>
                ))}
            </Box>
            {!collapse && hideConversions && !settings.debugFunnel && (
                <Box
                    sx={{
                        bottom: 0,
                        left: 0,
                        position: 'absolute',
                        right: 0,
                        textAlign: 'center',
                        top: 0
                    }}>
                    <svg height="100%" width={500} preserveAspectRatio="none" viewBox="0 0 500 500">
                        <path d="M 0 0 L 500 0 L 400 500 L 100 500" fill="#EFEFEB" />
                    </svg>
                </Box>
            )}
        </Box>
    );

}
